import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  ScrollView,
  } from 'react-native';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../../src/components/KeyboardAwareView';
import DatePickerModal from '../../../src/components/DatePickerModal';
import RecurrencePicker, { RecurrenceType } from '../../../src/components/RecurrencePicker';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { showAlert } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import { activitiesApi, goalsApi, recurrenceSchedulesApi, Goal } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import { recurrenceTypeToRrule } from '../../../src/utils/recurrence';
import type { SuggestionItem, SubActivityItem } from '../../../src/services/llm';
import { encrypt, decrypt, encryptTitle, mergeEncryptionMetadata, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { useTeamStore } from '../../../src/stores/teamStore';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { formatDisplayDate } from '../../../src/utils/format';
import { DURATION_OPTIONS_MINUTES } from '../../../src/constants';
import ScreenHeader from '../../../src/components/ScreenHeader';

export default function CreateActivityScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useThemedStyles(createStyles);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedGoalId, setSelectedGoalId] = useState<string | undefined>();
  const [estimatedMinutes, setEstimatedMinutes] = useState(60);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [scheduledDate, setScheduledDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [scheduledTime, setScheduledTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceType>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string | null>(null);
  const [showRecurrenceEndPicker, setShowRecurrenceEndPicker] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [aiSubactivities, setAiSubactivities] = useState<SubActivityItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);
  const llm = useOnDeviceLlm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGoalPicker, setShowGoalPicker] = useState(false);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  // Local map of decrypted goal titles for display
  const [goalTitles, setGoalTitles] = useState<Record<string, string>>({});
  // Decrypted goal end dates (YYYY-MM-DD) keyed by goal ID
  const [goalEndDates, setGoalEndDates] = useState<Record<string, string>>({});

  useEffect(() => {
    goalsApi.list().then(async (res) => {
      const goalList = res.data;
      setGoals(goalList);
      // Decrypt goal titles for picker display (auto-dispatches via team_id)
      const entries = await Promise.all(
        goalList.map(async (g): Promise<[string, string]> => [g.id, await decryptEntityTitle(g)])
      );
      setGoalTitles(Object.fromEntries(entries));
      // Decrypt goal end dates for recurrence schedule_end
      const endDateEntries = await Promise.all(
        goalList.map(async (g): Promise<[string, string] | null> => {
          if (!g.end_date_encrypted || !g.encryption_metadata?.end_date_iv) return null;
          try {
            const key = await getKeyForTeam(g.team_id);
            const endDate = await decrypt(g.end_date_encrypted, { iv: g.encryption_metadata.end_date_iv }, key);
            return [g.id, endDate];
          } catch { return null; }
        })
      );
      setGoalEndDates(Object.fromEntries(endDateEntries.filter((e): e is [string, string] => e !== null)));
    }).catch(() => {});
  }, []);

  const handleAiSuggest = async () => {
    if (!title.trim()) {
      showAlert('Add a Title', 'Enter an activity title first so AI can help optimize it.');
      return;
    }
    setShowAiResults(true);
    setAiSuggestions([]);
    setAiSubactivities([]);
    const result = await llm.suggest('activity', title.trim());
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  const handleAiSplit = async () => {
    if (!title.trim()) {
      showAlert('Add a Title', 'Enter an activity title first.');
      return;
    }
    setShowAiResults(true);
    setAiSuggestions([]);
    setAiSubactivities([]);
    const result = await llm.splitActivity(title.trim(), estimatedMinutes);
    if (result) {
      setAiSubactivities(result.suggested_activities);
    }
  };

  const selectedGoal = goals.find((g) => g.id === selectedGoalId);

  const handleSubmit = async () => {
    if (!requireConnectivity()) return;
    if (!title.trim()) {
      showAlert('Required', 'Please enter an activity title.');
      return;
    }

    setIsSubmitting(true);
    try {
      const key = await getKeyForTeam(activeTeamId);
      const { title_encrypted, title_iv } = await encryptTitle(title.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (description.trim()) {
        const descResult = await encrypt(description.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }
      const estMinResult = await encrypt(String(estimatedMinutes), key);

      const startTime = (() => {
        const offset = new Date().getTimezoneOffset();
        const sign = offset <= 0 ? '+' : '-';
        const absOff = Math.abs(offset);
        const hh = String(Math.floor(absOff / 60)).padStart(2, '0');
        const mm = String(absOff % 60).padStart(2, '0');
        return `${scheduledDate}T${scheduledTime}:00${sign}${hh}:${mm}`;
      })();

      await entityMutation(async () => {
        // Step 1: Create the activity (serves as template for recurring activities)
        const activityRes = await activitiesApi.create({
          title_encrypted,
          description_encrypted: descEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv, {
            estimated_minutes_iv: estMinResult.metadata.iv,
          }),
          goal_id: selectedGoalId,
          estimated_minutes_encrypted: estMinResult.encrypted,
          start_time: startTime,
        });

        // Step 2: If recurrence selected, create schedule linked to this activity
        const rrule = recurrenceTypeToRrule(recurrence);
        if (rrule) {
          const scheduleEnd = recurrenceEndDate ? `${recurrenceEndDate}T23:59:59` : undefined;
          await recurrenceSchedulesApi.create({
            rrule,
            schedule_start: startTime,
            schedule_end: scheduleEnd,
            activity_template_id: activityRes.data.id,
          });
        }
      });

      router.back();
    } catch {
      showAlert('Error', 'Failed to create activity.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScreenHeader
        title="New Activity"
        left={{
          label: 'Cancel',
          onPress: () => router.back(),
          testID: 'activity-create.cancel-button',
        }}
        right={{
          label: 'Save activity',
          text: 'Save',
          tone: 'primary',
          onPress: handleSubmit,
          disabled: isSubmitting || !title.trim(),
          loading: isSubmitting,
          testID: 'activity-create.save-button',
        }}
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} keyboardShouldPersistTaps="handled">
        {/* Title */}
        <Text style={styles.label}>TITLE *</Text>
        <TextInput
          style={styles.input}
          value={title}
          onChangeText={setTitle}
          placeholder="What needs to be done?"
          placeholderTextColor={colors.placeholder}
          autoFocus
          accessibilityLabel="Activity title"
          testID="activity-create.title-input"
        />

        {/* Linked Goal */}
        <Text style={styles.label}>LINKED GOAL</Text>
        <TouchableOpacity
          style={styles.selector}
          onPress={() => setShowGoalPicker(!showGoalPicker)}
          accessibilityLabel={selectedGoal ? `Linked goal: ${goalTitles[selectedGoal.id] || 'Untitled'}` : 'Select a goal (optional)'}
          accessibilityRole="button"
          testID="activity-create.goal-selector"
        >
          <Text style={styles.selectorText} accessible={false}>
            {selectedGoal ? `\u{1F3AF} ${goalTitles[selectedGoal.id] || 'Untitled'}` : 'Select a goal (optional)'}
          </Text>
          <Text style={styles.chevron} accessible={false}>{showGoalPicker ? '\u25B2' : '\u25BC'}</Text>
        </TouchableOpacity>

        {showGoalPicker && (
          <View style={styles.pickerList}>
            <TouchableOpacity
              style={[styles.pickerItem, !selectedGoalId && styles.pickerItemActive]}
              onPress={() => { setSelectedGoalId(undefined); setRecurrenceEndDate(null); setShowGoalPicker(false); }}
            >
              <Text style={styles.pickerItemText}>None</Text>
            </TouchableOpacity>
            {goals.map((g) => (
              <TouchableOpacity
                key={g.id}
                style={[styles.pickerItem, selectedGoalId === g.id && styles.pickerItemActive]}
                onPress={() => { setSelectedGoalId(g.id); if (goalEndDates[g.id]) setRecurrenceEndDate(goalEndDates[g.id]); setShowGoalPicker(false); }}
              >
                <Text style={styles.pickerItemText} numberOfLines={1}>{goalTitles[g.id] || 'Untitled'}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Scheduled Date */}
        <Text style={styles.label}>SCHEDULED DATE</Text>
        <TouchableOpacity
          style={styles.selector}
          onPress={() => setShowDatePicker(true)}
          accessibilityLabel={`Scheduled date: ${formatDisplayDate(scheduledDate)}`}
          accessibilityRole="button"
          testID="activity-create.date-selector"
        >
          <Text style={styles.selectorText} accessible={false}>{'\u{1F4C5}'} {formatDisplayDate(scheduledDate)}</Text>
        </TouchableOpacity>
        <DatePickerModal
          visible={showDatePicker}
          value={scheduledDate}
          onSelect={(d) => { setScheduledDate(d); setShowDatePicker(false); }}
          onClose={() => setShowDatePicker(false)}
          title="Schedule Activity"
        />

        {/* Time Picker */}
        <Text style={styles.label}>START TIME</Text>
        <TouchableOpacity
          style={styles.selector}
          onPress={() => setShowTimePicker(!showTimePicker)}
          accessibilityLabel={`Start time: ${(() => {
            const [h, m] = scheduledTime.split(':').map(Number);
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
          })()}`}
          accessibilityRole="button"
          testID="activity-create.time-selector"
        >
          <Text style={styles.selectorText} accessible={false}>
            {'\u{1F552}'} {(() => {
              const [h, m] = scheduledTime.split(':').map(Number);
              const ampm = h >= 12 ? 'PM' : 'AM';
              const h12 = h % 12 || 12;
              return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
            })()}
          </Text>
          <Text style={styles.chevron} accessible={false}>{showTimePicker ? '\u25B2' : '\u25BC'}</Text>
        </TouchableOpacity>

        {showTimePicker && (
          <View style={styles.pickerList}>
            {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((h) =>
              [0, 30].map((m) => {
                const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                const ampm = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 || 12;
                const label = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                return (
                  <TouchableOpacity
                    key={val}
                    style={[styles.pickerItem, scheduledTime === val && styles.pickerItemActive]}
                    onPress={() => { setScheduledTime(val); setShowTimePicker(false); }}
                  >
                    <Text style={styles.pickerItemText}>{label}</Text>
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        )}

        {/* Recurrence */}
        <Text style={styles.label}>REPEAT</Text>
        <RecurrencePicker value={recurrence} onChange={setRecurrence} />

        {recurrence !== 'none' && (
          <>
            <Text style={styles.label}>REPEATS UNTIL</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                style={[styles.selector, { flex: 1 }]}
                onPress={() => setShowRecurrenceEndPicker(true)}
                accessibilityLabel={recurrenceEndDate ? `Repeats until ${formatDisplayDate(recurrenceEndDate)}` : 'No end date'}
                accessibilityRole="button"
                testID="activity-create.recurrence-end-selector"
              >
                <Text style={styles.selectorText} accessible={false}>
                  {recurrenceEndDate ? formatDisplayDate(recurrenceEndDate) : 'No end date'}
                </Text>
              </TouchableOpacity>
              {recurrenceEndDate && (
                <TouchableOpacity
                  style={[styles.selector, { paddingHorizontal: 12 }]}
                  onPress={() => setRecurrenceEndDate(null)}
                  accessibilityLabel="Clear recurrence end date"
                  accessibilityRole="button"
                >
                  <Text style={{ fontSize: 14, color: colors.textMuted }} accessible={false}>{'\u2715'}</Text>
                </TouchableOpacity>
              )}
            </View>
            <DatePickerModal
              visible={showRecurrenceEndPicker}
              value={recurrenceEndDate || scheduledDate}
              onSelect={(d) => { setRecurrenceEndDate(d); setShowRecurrenceEndPicker(false); }}
              onClose={() => setShowRecurrenceEndPicker(false)}
              title="Repeats Until"
            />
          </>
        )}

        {/* Estimated Duration */}
        <Text style={styles.label}>ESTIMATED DURATION</Text>
        <View style={styles.durationRow} accessibilityRole="radiogroup" accessibilityLabel="Estimated duration">
          {DURATION_OPTIONS_MINUTES.map((mins) => (
            <Pressable
              key={mins}
              style={[styles.durationChip, estimatedMinutes === mins && styles.durationChipActive]}
              onPress={() => setEstimatedMinutes(mins)}
              accessibilityRole="button"
              accessibilityState={{ checked: estimatedMinutes === mins, selected: estimatedMinutes === mins }}
              aria-checked={estimatedMinutes === mins}
              aria-selected={estimatedMinutes === mins}
              accessibilityLabel={mins < 60 ? `${mins} minutes` : `${mins / 60} hours`}
              testID={`activity-create.duration-${mins}`}
            >
              <Text style={[
                styles.durationChipText,
                estimatedMinutes === mins && styles.durationChipTextActive,
              ]} accessible={false}>
                {mins < 60 ? `${mins}m` : `${mins / 60}h`}
              </Text>
            </Pressable>
          ))}
        </View>
        {estimatedMinutes > 90 && (
          <View style={styles.warningBox}>
            <Text style={styles.warningText}>
              {'\u26A0'} Activities longer than 90 minutes are harder to complete. Consider breaking this into smaller activities.
            </Text>
          </View>
        )}

        {/* Description */}
        <Text style={styles.label}>DESCRIPTION</Text>
        <TextInput
          style={styles.textArea}
          value={description}
          onChangeText={setDescription}
          placeholder="Add details about this activity..."
          placeholderTextColor={colors.placeholder}
          multiline
          textAlignVertical="top"
          accessibilityLabel="Activity description"
          testID="activity-create.description-input"
        />
        {/* AI Buttons */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <View style={styles.aiRow}>
            <TouchableOpacity
              style={[styles.aiButton, { flex: 1 }]}
              onPress={handleAiSuggest}
              disabled={llm.loading}
              accessibilityLabel="Get AI suggestions"
              accessibilityRole="button"
              testID="activity-create.ai-suggest-button"
            >
              <Text style={styles.aiButtonText} accessible={false}>{'\u{1F9E0}'} AI Suggest</Text>
            </TouchableOpacity>
            {estimatedMinutes > 90 && (
              <TouchableOpacity
                style={[styles.aiButton, { flex: 1 }]}
                onPress={handleAiSplit}
                disabled={llm.loading}
                accessibilityLabel="Split activity with AI"
                accessibilityRole="button"
                testID="activity-create.ai-split-button"
              >
                <Text style={styles.aiButtonText} accessible={false}>{'\u2702\uFE0F'} AI Split</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            subtasks={aiSubactivities}
            error={llm.error}
            onApplySuggestion={(text) => {
              setTitle(text);
              setShowAiResults(false);
              setAiSuggestions([]);
              setAiSubactivities([]);
            }}
            onAddSubtask={(subtask) => {
              showAlert('Sub-activity Added', `"${subtask.text}" (${subtask.estimated_minutes}m) — create it as a separate activity after saving this one.`);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
              setAiSubactivities([]);
            }}
          />
        )}
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  label: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 20 },
  input: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 16, color: theme.colors.textPrimary },
  selector: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14 },
  selectorText: { fontSize: 15, color: theme.colors.textSecondary, flex: 1 },
  chevron: { fontSize: 12, color: theme.colors.textDisabled, marginLeft: 8 },
  pickerList: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, marginTop: 4, overflow: 'hidden' as const },
  pickerItem: { paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.borderLight },
  pickerItemActive: { backgroundColor: theme.colors.primaryBg },
  pickerItemText: { fontSize: 14, color: theme.colors.textSecondary },
  durationRow: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 8 },
  durationChip: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, backgroundColor: theme.colors.bgTertiary },
  durationChipActive: { backgroundColor: theme.colors.primary },
  durationChipText: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textMuted },
  durationChipTextActive: { color: theme.colors.textOnPrimary },
  warningBox: { backgroundColor: theme.colors.warningBg, borderWidth: 1, borderColor: theme.colors.warningBorder, borderRadius: 10, padding: 12, marginTop: 8 },
  warningText: { fontSize: 13, color: theme.colors.warningText, lineHeight: 18 },
  textArea: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 15, color: theme.colors.textPrimary, minHeight: 100 },
  aiRow: { flexDirection: 'row' as const, gap: 8, marginTop: 16 },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const },
  aiButtonText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.ai },
});
